<template>
    <div class="CTags">
        <div class="flex flex-wrap items-center" :class="[['gray', 'white'].includes(props.variant) ? 'gap-2' : '']">
            <template v-for="(item, index) in normalizedItems" :key="item.label">
                <CPill
                    :class="{
                        'hover:shadow-lg hover:rounded-full': ['gray', 'white'].includes(props.variant) && hasTagClickHandler
                    }"
                    :green-icon="props.highlighted.includes(item.label)"
                    :icon="props.highlighted.includes(item.label) ? 'checkmark' : item.icon"
                    :text="item.label"
                    :title="item.title"
                    :variant="props.variant"
                    :show-comma="props.variant === 'plain' && (index < normalizedItems.length - 1 || remainingCount > 0)"
                    @click="() => handleTagClick(item)"
                >
                    <template v-if="props.tooltips?.[item.label]" #end>
                        <CTooltip class="self-center">{{ props.tooltips[item.label] }}</CTooltip>
                    </template>
                </CPill>
            </template>
            <span v-if="remainingCount > 0" class="text-sm text-gray-500 dark:text-txt-body">+{{ remainingCount }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import CPill from '@/components/ui/CPill.vue'
import CTooltip from '@/components/ui/CTooltip.vue'
import { type Extractor, retrieveValue } from '@/utils/normalizeOption'
import { computed } from 'vue'

type Variants = 'gray' | 'white' | 'plain'

interface Item {
    tag: any
    label: string
    title: string
    icon?: string
}

interface Props {
    tags?: any[]
    highlighted?: any[]
    tooltips?: Record<PropertyKey, string>
    variant?: Variants
    title?: string
    tagLabelKey?: Extractor
    tagTitleKey?: Extractor
    tagIconKey?: Extractor
    tagTooltipKey?: Extractor
    maxTags?: number
}

const props = withDefaults(defineProps<Props>(), {
    tags: () => [],
    highlighted: () => [],
    tooltips: () => ({}),
    variant: () => 'white',
    title: () => '',
    tagLabelKey: undefined,
    tagTitleKey: undefined,
    tagIconKey: undefined,
    tagTooltipKey: undefined,
    maxTags: 5
})

const emit = defineEmits<{
    (e: 'tag-click', tag: any): void
}>()

const hasTagClickHandler = computed(() => true)

const normalizedItems = computed((): Item[] => {
    const tags = props.tags || []
    const limitedTags = props.maxTags > 0 ? tags.slice(0, props.maxTags) : tags
    return limitedTags.map((tag) => ({
        tag,
        label: props.tagLabelKey ? retrieveValue(tag, props.tagLabelKey) : tag,
        title: props.tagTitleKey ? retrieveValue(tag, props.tagTitleKey) : '',
        icon: props.tagIconKey ? retrieveValue(tag, props.tagIconKey) : undefined
    }))
})

const remainingCount = computed(() => {
    const tags = props.tags || []
    return props.maxTags > 0 ? Math.max(0, tags.length - props.maxTags) : 0
})

const handleTagClick = (item: Item) => {
    emit('tag-click', item.tag)
}
</script>
